import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Megaphone, Plus, Pencil, Trash2, X, Save, Mail, MessageSquare, Newspaper, Users, UserPlus, Clock, Send, TestTube, Eye, LayoutGrid, List as ListIcon, Database, ShieldCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useUiState } from "@/lib/uiState";

const AUDIENCE = [
  { value: "all", label: "All Alumni", icon: Users },
  { value: "new_alumni", label: "New Alumni", icon: UserPlus },
  { value: "reconnect", label: "Reconnect / Lapsed", icon: Clock },
  { value: "no_email", label: "Missing Email", icon: Mail },
  { value: "admin", label: "Administrators", icon: ShieldCheck },
];
const CHANNEL = [
  { value: "email", label: "Email", icon: Mail },
  { value: "sms", label: "SMS", icon: MessageSquare },
  { value: "newsletter", label: "Newsletter", icon: Newspaper },
  { value: "announcement", label: "Announcement", icon: Megaphone },
  { value: "system", label: "System Log", icon: Database },
];

const empty = { title: "", subject: "", body: "", audience: "all", channel: "email", status: "draft" };

export default function Communications() {
  const { isAdmin } = useAuth();
  const [comms, setComms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // comm object or empty for new
  const [testFor, setTestFor] = useState(null); // comm being test-sent
  const [testEmail, setTestEmail] = useState("bschofield@e-musclecars.com");
  const [sendingId, setSendingId] = useState(null);
  const [preview, setPreview] = useState(null); // {title, subject, html, id}
  const [logDetail, setLogDetail] = useState(null); // activity-log entry being viewed
  const [recipients, setRecipients] = useState([]);
  const [excluded, setExcluded] = useState({});
  const [allowResend, setAllowResend] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("ud_comms_view") || "grid");
  const setViewMode = (v) => { setView(v); localStorage.setItem("ud_comms_view", v); };
  const [allowOpen, setAllowOpen] = useUiState("ud_comms_allow_open", true);
  const [tplOpen, setTplOpen] = useUiState("ud_comms_tpl_open", true);
  const [actOpen, setActOpen] = useUiState("ud_comms_activity_open", true);

  const openPreview = async (c) => {
    try {
      const { data } = await api.get(`/communications/${c.id}/preview`);
      setPreview({ id: c.id, title: c.title, subject: data.subject, html: data.html });
      setExcluded({});
      setRecipients([]);
      setAllowResend(false);
      api.get(`/communications/${c.id}/recipients`).then((r) => setRecipients(r.data.recipients)).catch(() => setRecipients([]));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const sendFromPreview = async () => {
    const exclude = recipients.filter((r) => excluded[r.id]).map((r) => r.id);
    const selectedCount = recipients.length - exclude.length;
    if (selectedCount <= 0) { toast.error("Select at least one recipient"); return; }
    if (!window.confirm(`Send "${preview.title}" to ${selectedCount} selected recipient(s)?`)) return;
    setSendingId(preview.id);
    try {
      const { data } = await api.post(`/communications/${preview.id}/send${allowResend ? "?force=true" : ""}`, { exclude });
      toast.success(`Sent to ${data.sent}${data.skipped ? `, skipped ${data.skipped} already sent` : ""}${data.failed ? `, ${data.failed} failed` : ""}`);
      setPreview(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSendingId(null);
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/communications").then((r) => setComms(r.data.communications)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const [emailOn, setEmailOn] = useState(true);
  const [savingEmail, setSavingEmail] = useState(false);
  const [allowlist, setAllowlist] = useState([]);
  const [allowEmail, setAllowEmail] = useState("");
  useEffect(() => { api.get("/settings/email-sending").then((r) => { setEmailOn(r.data.sending_enabled); setAllowlist(r.data.test_allowlist || []); }).catch(() => {}); }, []);
  const saveAllowlist = async (next) => {
    try { const { data } = await api.patch("/settings/email-sending", { test_allowlist: next }); setAllowlist(data.test_allowlist || []); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addTest = () => { const e = allowEmail.trim().toLowerCase(); if (!e) return; if (!allowlist.includes(e)) saveAllowlist([...allowlist, e]); setAllowEmail(""); };
  const toggleEmail = async (next) => {
    if (!next && !window.confirm("Turn ALL outbound email OFF? While off, nobody will receive any email — invites, newsletters, receipts, password resets, everything is blocked. Use this while testing.")) return;
    setSavingEmail(true);
    try {
      const { data } = await api.patch("/settings/email-sending", { sending_enabled: next });
      setEmailOn(data.sending_enabled);
      toast.success(data.sending_enabled ? "Email sending is ON" : "Email sending is OFF — no emails will be sent");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingEmail(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this communication?")) return;
    await api.delete(`/communications/${id}`);
    toast.success("Communication deleted");
    load();
  };

  const sendTest = async () => {
    try {
      await api.post(`/communications/${testFor.id}/send-test`, { email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
      setTestFor(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const sendToAudience = async (c, force = false) => {
    const msg = force
      ? `Re-send "${c.title}" to EVERYONE in this audience again (including those who already received it)?`
      : `Send "${c.title}" to matching alumni who haven't received it yet?`;
    if (!window.confirm(msg)) return;
    setSendingId(c.id);
    try {
      const { data } = await api.post(`/communications/${c.id}/send${force ? "?force=true" : ""}`);
      if (data.sent === 0 && data.skipped > 0) {
        toast.info(`No new emails sent — all ${data.skipped} recipient(s) already received this. Use "Resend to all" to send again.`);
      } else {
        toast.success(`Sent to ${data.sent} alumni${data.skipped ? `, skipped ${data.skipped} already sent` : ""}${data.failed ? `, ${data.failed} failed` : ""}`);
      }
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSendingId(null);
    }
  };

  // Templates/campaigns you compose & send (top area) vs. the activity log of what actually went out.
  const templates = comms.filter((c) => c.channel !== "system");
  const activity = comms
    .filter((c) => c.last_sent_at || c.channel === "system")
    .sort((a, b) => (b.last_sent_at || b.created_at || "").localeCompare(a.last_sent_at || a.created_at || ""));

  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Outreach</div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-1">Communications</h1>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button data-testid="add-communication" onClick={() => setEditing({ ...empty })} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">
              <Plus className="h-4 w-4" /> New Communication
            </button>
          )}
        </div>
      </div>

      {loading && <div className="text-muted-foreground">Loading…</div>}

      {isAdmin && (
        <div data-testid="email-master-switch" className={`flex items-center justify-between gap-4 mb-6 rounded-lg border p-4 ${emailOn ? "bg-card border-border" : "bg-rose-50 border-rose-300"}`}>
          <div className="flex items-start gap-3">
            <Mail className={`h-5 w-5 mt-0.5 ${emailOn ? "text-primary" : "text-rose-600"}`} />
            <div>
              <div className="font-medium text-sm">Master email switch — <span data-testid="email-switch-state" className={emailOn ? "text-emerald-700" : "text-rose-700"}>{emailOn ? "ON (emails will send)" : "OFF (all email blocked)"}</span></div>
              <div className="text-xs text-muted-foreground mt-0.5">{emailOn ? "Turn OFF while testing so no one gets an email by accident — this blocks every send, including invites, receipts and password resets." : "No outbound email of any kind will be delivered while this is off. Turn back on to resume sending."}</div>
            </div>
          </div>
          <button data-testid="email-switch-toggle" disabled={savingEmail} onClick={() => toggleEmail(!emailOn)}
            className={`shrink-0 px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60 ${emailOn ? "bg-rose-600 text-white hover:bg-rose-700" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}>
            {savingEmail ? "Saving…" : emailOn ? "Turn all email OFF" : "Turn email ON"}
          </button>
        </div>
      )}

      {isAdmin && !emailOn && (
        <div data-testid="email-test-allowlist" className="mb-6 rounded-lg border border-border bg-card p-4">
          <button data-testid="allowlist-collapse" onClick={() => setAllowOpen((o) => !o)} className="w-full flex items-center gap-2 text-left">
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${allowOpen ? "" : "-rotate-90"}`} />
            <span className="text-sm font-medium">Test allowlist — send to just these addresses</span>
          </button>
          {allowOpen && (<div className="mt-3">
          <p className="text-xs text-muted-foreground mb-3">While the master switch is OFF, only the addresses below will receive email. Everyone else stays blocked, so you can safely test with one or two alumni.</p>
          <div className="flex gap-2 mb-3">
            <input data-testid="allowlist-input" value={allowEmail} onChange={(e) => setAllowEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTest()} placeholder="alumnus@example.com" className="ud-input flex-1" />
            <button data-testid="allowlist-add" onClick={addTest} className="px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90">Add</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {allowlist.length === 0 && <span className="text-xs text-muted-foreground">No test recipients yet — no email will go out at all.</span>}
            {allowlist.map((e) => (
              <span key={e} data-testid={`allowlist-chip-${e}`} className="inline-flex items-center gap-1.5 text-xs bg-emerald-100 text-emerald-800 rounded-full pl-3 pr-1.5 py-1">
                {e}
                <button data-testid={`allowlist-remove-${e}`} onClick={() => saveAllowlist(allowlist.filter((x) => x !== e))} className="rounded-full hover:bg-emerald-200 p-0.5"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          </div>)}
        </div>
      )}

      {!loading && (
        <div data-testid="comms-templates" className="mb-2">
          <div className="flex items-center justify-between gap-3 mb-3">
            <button data-testid="templates-collapse" onClick={() => setTplOpen((o) => !o)} className="flex items-center gap-2 text-left min-w-0">
              <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform shrink-0 ${tplOpen ? "" : "-rotate-90"}`} />
              <h2 className="font-serif text-xl tracking-tight">Communications</h2>
              <span className="text-xs text-muted-foreground">({templates.length})</span>
            </button>
            <div className="flex rounded-md border border-border overflow-hidden shrink-0">
              <button data-testid="comms-view-grid" onClick={() => setViewMode("grid")} title="Grid view" className={`p-2 ${view === "grid" ? "bg-primary text-white" : "bg-card text-muted-foreground hover:bg-muted"}`}><LayoutGrid className="h-4 w-4" /></button>
              <button data-testid="comms-view-list" onClick={() => setViewMode("list")} title="List view" className={`p-2 border-l border-border ${view === "list" ? "bg-primary text-white" : "bg-card text-muted-foreground hover:bg-muted"}`}><ListIcon className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      )}

      {tplOpen && view === "list" && !loading && templates.length > 0 && (
        <div data-testid="comms-list" className="bg-card border border-border rounded-lg shadow-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-mono">
              <tr>
                <th className="text-left px-4 py-2.5">Title</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">Channel</th>
                <th className="text-left px-4 py-2.5 hidden lg:table-cell">Audience</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">Last Sent</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {templates.map((c) => {
                const aud = AUDIENCE.find((a) => a.value === c.audience) || AUDIENCE[0];
                const ch = CHANNEL.find((x) => x.value === c.channel) || CHANNEL[0];
                return (
                  <tr key={c.id} data-testid={`comm-row-${c.id}`} className="hover:bg-muted/40">
                    <td className="px-4 py-2.5"><div className="font-medium">{c.title}</div><div className="text-xs text-muted-foreground truncate max-w-xs">{c.subject}</div></td>
                    <td className="px-4 py-2.5 text-muted-foreground hidden md:table-cell">{ch.label}</td>
                    <td className="px-4 py-2.5 text-muted-foreground hidden lg:table-cell">{aud.label}</td>
                    <td className="px-4 py-2.5"><span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-muted">{c.status}</span></td>
                    <td className="px-4 py-2.5 text-muted-foreground hidden md:table-cell whitespace-nowrap">{c.last_sent_at ? `${c.last_sent_count} · ${new Date(c.last_sent_at).toLocaleDateString()}` : "—"}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {c.channel !== "system" && <button data-testid={`preview-comm-${c.id}`} onClick={() => openPreview(c)} title="Preview" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary"><Eye className="h-4 w-4" /></button>}
                        {isAdmin && c.channel !== "system" && <button onClick={() => { setTestFor(c); }} title="Send test" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary"><TestTube className="h-4 w-4" /></button>}
                        {isAdmin && c.channel !== "system" && <button data-testid={`send-audience-${c.id}`} onClick={() => sendToAudience(c)} disabled={sendingId === c.id} title="Send" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary disabled:opacity-50"><Send className="h-4 w-4" /></button>}
                        {isAdmin && c.channel !== "system" && <button onClick={() => setEditing(c)} title="Edit" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>}
                        {isAdmin && <button onClick={() => del(c.id)} title="Delete" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tplOpen && view === "grid" && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {templates.map((c) => {
          const aud = AUDIENCE.find((a) => a.value === c.audience) || AUDIENCE[0];
          const ch = CHANNEL.find((x) => x.value === c.channel) || CHANNEL[0];
          const ChIcon = ch.icon;
          const AudIcon = aud.icon;
          return (
            <div key={c.id} data-testid={`comm-card-${c.id}`} className="bg-card border border-border rounded-lg shadow-sm p-5 flex flex-col hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">
                    <ChIcon className="h-3.5 w-3.5" /> {ch.label}
                    <span className="px-1.5 py-0.5 rounded bg-muted text-[10px]">{c.status}</span>
                  </div>
                  <h3 className="font-serif text-xl tracking-tight">{c.title}</h3>
                  <div className="text-sm font-medium text-primary mt-0.5">{c.subject}</div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1 shrink-0">
                    {c.channel !== "system" && <button data-testid={`preview-comm-${c.id}`} onClick={() => openPreview(c)} title="Preview email" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary"><Eye className="h-4 w-4" /></button>}
                    {c.channel !== "system" && <button data-testid={`edit-comm-${c.id}`} onClick={() => setEditing(c)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>}
                    <button data-testid={`delete-comm-${c.id}`} onClick={() => del(c.id)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
                {!isAdmin && c.channel !== "system" && (
                  <button data-testid={`preview-comm-${c.id}`} onClick={() => openPreview(c)} title="Preview email" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary shrink-0"><Eye className="h-4 w-4" /></button>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-3 whitespace-pre-line line-clamp-4 flex-1">{c.body}</p>
              <div className="mt-4 pt-3 border-t border-border flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-2"><AudIcon className="h-3.5 w-3.5" /> {aud.label}</span>
                {c.last_sent_at && <span className="text-emerald-700">Sent to {c.last_sent_count} · {new Date(c.last_sent_at).toLocaleDateString()}</span>}
              </div>
              {isAdmin && c.channel !== "system" && (
                <div className="mt-3 flex gap-2">
                  <button data-testid={`preview-btn-${c.id}`} onClick={() => openPreview(c)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-border text-sm hover:bg-muted transition-colors">
                    <Eye className="h-4 w-4" /> Preview
                  </button>
                  <button data-testid={`send-test-${c.id}`} onClick={() => { setTestFor(c); }} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-border text-sm hover:bg-muted transition-colors">
                    <TestTube className="h-4 w-4" /> Send test
                  </button>
                  <button data-testid={`send-audience-${c.id}`} onClick={() => sendToAudience(c)} disabled={sendingId === c.id} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">
                    <Send className="h-4 w-4" /> {sendingId === c.id ? "Sending…" : "Send"}
                  </button>
                  {c.last_sent_at && (
                    <button data-testid={`resend-all-${c.id}`} onClick={() => sendToAudience(c, true)} disabled={sendingId === c.id} title="Resend to everyone, including those who already received it" className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-border text-sm hover:bg-muted transition-colors disabled:opacity-60">
                      <Send className="h-4 w-4" /> Resend all
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {!loading && templates.length === 0 && <div className="text-muted-foreground">No communications yet.</div>}
      </div>
      )}

      {!loading && (
        <div data-testid="comms-activity" className="mt-8">
          <button data-testid="activity-collapse" onClick={() => setActOpen((o) => !o)} className="flex items-center gap-2 mb-3 text-left">
            <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${actOpen ? "" : "-rotate-90"}`} />
            <Clock className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-serif text-xl tracking-tight">Communication Activity</h2>
            <span className="text-xs text-muted-foreground">({activity.length})</span>
          </button>
          {actOpen && (
          <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
            {activity.length === 0 ? (
              <div className="px-4 py-4 text-sm text-muted-foreground">Nothing sent yet. Sent emails and system events (like emailed backups) will appear here.</div>
            ) : (
              <div className="divide-y divide-border">
                {activity.map((c) => {
                  const ch = CHANNEL.find((x) => x.value === c.channel) || CHANNEL[0];
                  const ChIcon = ch.icon;
                  const count = c.last_sent_count != null ? c.last_sent_count : (c.sent_to ? c.sent_to.length : 0);
                  return (
                    <button key={c.id} data-testid={`activity-row-${c.id}`} onClick={() => setLogDetail(c)} className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors">
                      <span className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${c.channel === "system" ? "bg-slate-100 text-slate-600" : "bg-primary/10 text-primary"}`}><ChIcon className="h-4 w-4" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium truncate">{c.title}</span>
                        <span className="block text-xs text-muted-foreground truncate">{c.subject}</span>
                      </span>
                      <span className="shrink-0 text-right text-xs text-muted-foreground">
                        <span className="block">{c.last_sent_at ? new Date(c.last_sent_at).toLocaleDateString() : "—"}</span>
                        <span className="block">{c.last_sent_at ? new Date(c.last_sent_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}</span>
                        <span className="block text-emerald-700">Sent to {count}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          )}
        </div>
      )}

      {editing && <CommEditor comm={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {logDetail && createPortal((
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/60" onMouseDown={(e) => { if (e.target === e.currentTarget) setLogDetail(null); }}>
          <div className="bg-card w-full max-w-lg rounded-lg shadow-xl border border-border flex flex-col" style={{ maxHeight: "calc(100vh - 2rem)", overflow: "hidden" }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <div className="min-w-0">
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{(CHANNEL.find((x) => x.value === logDetail.channel) || CHANNEL[0]).label} · {logDetail.last_sent_at ? new Date(logDetail.last_sent_at).toLocaleString() : "—"}</div>
                <h2 className="font-serif text-lg tracking-tight truncate">{logDetail.title}</h2>
              </div>
              <button data-testid="activity-detail-close" onClick={() => setLogDetail(null)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground shrink-0"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 overflow-auto space-y-4">
              {logDetail.subject && <div><div className="text-xs text-muted-foreground">Subject</div><div className="text-sm font-medium">{logDetail.subject}</div></div>}
              {logDetail.body && <div><div className="text-xs text-muted-foreground mb-1">Details</div><div className="text-sm whitespace-pre-wrap text-foreground/90">{logDetail.body}</div></div>}
              <div>
                <div className="text-xs text-muted-foreground mb-1">Sent to{(logDetail.sent_to || []).length ? ` (${logDetail.sent_to.length})` : ""}</div>
                {(logDetail.sent_to || []).length ? (
                  <div className="border border-border rounded-md divide-y divide-border max-h-48 overflow-auto">
                    {logDetail.sent_to.map((r, i) => (
                      <div key={i} data-testid={`activity-recipient-${i}`} className="px-3 py-1.5 text-sm text-foreground/90">{r}</div>
                    ))}
                  </div>
                ) : <div className="text-sm text-muted-foreground">No recipient list recorded.</div>}
              </div>
            </div>
            {isAdmin && (
              <div className="px-6 py-3 border-t border-border flex justify-end shrink-0">
                <button data-testid="activity-detail-delete" onClick={() => { del(logDetail.id); setLogDetail(null); }} className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-sm text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /> Delete this log</button>
              </div>
            )}
          </div>
        </div>
      ), document.body)}

      {preview && createPortal((
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/60" onMouseDown={(e) => { if (e.target === e.currentTarget) setPreview(null); }}>
          <div className="bg-card w-full max-w-2xl rounded-lg shadow-xl border border-border flex flex-col" style={{ maxHeight: "calc(100vh - 2rem)", overflow: "hidden" }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <div className="min-w-0">
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Email Preview</div>
                <h2 className="font-serif text-lg tracking-tight truncate">Subject: {preview.subject}</h2>
              </div>
              <button data-testid="preview-close" onClick={() => setPreview(null)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground shrink-0"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 overflow-auto bg-slate-100 flex-1 min-h-0">
              <iframe data-testid="preview-frame" title="Email preview" srcDoc={preview.html} className="w-full h-[42vh] bg-white rounded-md border border-border" sandbox="" />
              <div className="mt-4 bg-card rounded-md border border-border">
                <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
                  <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Recipients</span>
                  <span className="text-xs text-muted-foreground" data-testid="preview-recipient-count">{recipients.filter((r) => !excluded[r.id]).length} of {recipients.length} selected</span>
                </div>
                <div className="max-h-40 overflow-y-auto divide-y divide-border">
                  {recipients.length === 0 && <div className="px-4 py-4 text-sm text-muted-foreground">No recipients with a valid email match this audience.</div>}
                  {recipients.map((r) => (
                    <label key={r.id} data-testid={`recipient-${r.id}`} className="flex items-center gap-3 px-4 py-2 hover:bg-muted/50 cursor-pointer text-sm">
                      <input type="checkbox" data-testid={`recipient-check-${r.id}`} checked={!excluded[r.id]} onChange={(e) => setExcluded((p) => ({ ...p, [r.id]: !e.target.checked }))} className="h-4 w-4 accent-[#001A4B]" />
                      <span className="flex-1 min-w-0">
                        <span className="font-medium">{r.name || "—"}</span>
                        <span className="text-muted-foreground"> · {r.email}</span>
                      </span>
                      {r.claimed && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Claimed</span>}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-border flex items-center justify-between gap-3">
              <div className="flex flex-col gap-1.5">
                <label data-testid="allow-resend-toggle" className="flex items-center gap-2 text-xs cursor-pointer select-none">
                  <input type="checkbox" data-testid="allow-resend-check" checked={allowResend} onChange={(e) => setAllowResend(e.target.checked)} className="h-4 w-4 accent-[#001A4B]" />
                  <span className="font-medium text-foreground">Allow resend</span>
                  <span className="text-muted-foreground">— ignore "already sent" and email everyone selected</span>
                </label>
                <span className="text-xs text-muted-foreground">Sample first name "Alex". Uncheck anyone you don't want to email.</span>
              </div>
              <button data-testid="preview-send" onClick={sendFromPreview} disabled={sendingId === preview.id || recipients.length === 0} className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60 shrink-0">
                <Send className="h-4 w-4" /> {sendingId === preview.id ? "Sending…" : `Send to ${recipients.filter((r) => !excluded[r.id]).length}`}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {testFor && createPortal((
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/60" onMouseDown={(e) => { if (e.target === e.currentTarget) setTestFor(null); }}>
          <div className="bg-card w-full max-w-md rounded-lg shadow-xl border border-border" style={{ maxHeight: "calc(100vh - 2rem)", overflow: "auto" }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="font-serif text-xl tracking-tight">Send Test Email</h2>
              <button data-testid="test-close" onClick={() => setTestFor(null)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">Send a one-off test copy of <b>{testFor.title}</b> to any address to preview it.</p>
              <div>
                <label className="block text-sm font-medium mb-1">Recipient email</label>
                <input data-testid="test-email-input" type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
              <button onClick={() => setTestFor(null)} className="px-4 py-2.5 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
              <button data-testid="send-test-submit" onClick={sendTest} className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90">
                <Send className="h-4 w-4" /> Send Test
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}

function CommEditor({ comm, onClose, onSaved }) {
  const [c, setC] = useState(comm);
  const [busy, setBusy] = useState(false);
  const isNew = !comm.id;
  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!c.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      if (isNew) await api.post("/communications", c);
      else await api.put(`/communications/${c.id}`, c);
      toast.success(isNew ? "Communication created" : "Communication updated");
      onSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return createPortal((
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/60" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-card w-full max-w-lg rounded-lg shadow-xl border border-border overflow-y-auto" style={{ maxHeight: "calc(100vh - 2rem)" }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card">
          <h2 className="font-serif text-xl tracking-tight">{isNew ? "New Communication" : "Edit Communication"}</h2>
          <button data-testid="comm-close" onClick={onClose} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input data-testid="comm-title" value={c.title} onChange={(e) => set("title", e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subject line</label>
            <input data-testid="comm-subject" value={c.subject} onChange={(e) => set("subject", e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Channel</label>
              <select data-testid="comm-channel" value={c.channel} onChange={(e) => set("channel", e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none">
                {CHANNEL.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Audience</label>
              <select data-testid="comm-audience" value={c.audience} onChange={(e) => set("audience", e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none">
                {AUDIENCE.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Message body <span className="text-muted-foreground font-normal">(use {"{first_name}"} for personalization)</span></label>
            <textarea data-testid="comm-body" value={c.body} onChange={(e) => set("body", e.target.value)} rows={8} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none resize-y" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2 sticky bottom-0 bg-card">
          <button onClick={onClose} className="px-4 py-2.5 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
          <button data-testid="save-communication" onClick={save} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
